import React from "react";
import "./hero-seat.css";

// Map single letter suits to full names
const SUIT_MAP = {
  'h': 'hearts',
  'd': 'diamonds',
  'c': 'clubs',
  's': 'spades'
};

const HeroSeat = ({ 
  blinds,
  isDealer,
  bet = 0,
  card1Value,
  card1Suit,
  card2Value,
  card2Suit,
  potSize = "0",
  showPotWon = false,
  onPotWonChange = () => {},
  title,
  betType,
  isHeroOpener,
  gameConfig
}) => {
  const initialBlinds = parseFloat(blinds);
  
  // Enhanced logic to determine if bet should be shown
  const shouldShowBet = (() => {
    // Don't show bets when in pre-decision states
    if (betType === 'OPEN') return false;
    if (betType === 'FACING_OPEN_3BET') return false;
    
    // For 3bet/4bet scenarios, we need special handling
    if (betType === 'FACING_3BET_4BET' || betType === 'FACING_3BET_CALL') {
      // If this is the hero and hero is the opener, show the open bet
      if (isHeroOpener && gameConfig?.heroPosition === title) {
        return true;
      }
    }
    
    // Default case: show only if there's a positive bet amount
    return bet > 0;
  })();
  
  // Calculate the correct bet to display
  let displayBet = bet;
  
  // For 3bet/4bet scenarios, if hero is the opener, use correct open size
  if ((betType === 'FACING_3BET_4BET' || betType === 'FACING_3BET_CALL') && 
      isHeroOpener && 
      gameConfig?.heroPosition === title) {
    
    // Special case for SB open size
    if (title === 'SB') {
      displayBet = 3;
    } else {
      displayBet = 2.5;
    }
  }
  
  // For OPEN scenarios, if hero is SB, use 3bb
  if (betType === 'OPEN' && shouldShowBet && title === 'SB') {
    displayBet = 3;
  }
  
  // Calculation of remaining blinds
  const remainingBlinds = shouldShowBet && displayBet > 0 ? 
    (initialBlinds - displayBet).toFixed(1) : blinds;

  // Convert single letter suits to full names
  const fullCard1Suit = SUIT_MAP[card1Suit] || card1Suit;
  const fullCard2Suit = SUIT_MAP[card2Suit] || card2Suit;

  React.useEffect(() => {
    onPotWonChange(showPotWon);
  }, [showPotWon, onPotWonChange]);

  return (
    <div className="hero-seat">
      <div className="hero-title">{title}</div>
      <div className="hero-big-blinds">{remainingBlinds}</div>
      
      <div className="cards-container">
        <div className="card card-1">
          <img 
            src={`/svgs/${fullCard1Suit}.svg`}
            alt={fullCard1Suit} 
            className="card-suit"
            style={{ color: getSuitColor(fullCard1Suit) }}
          />
          <span 
            className="card-value"
            style={{ color: getSuitColor(fullCard1Suit) }}
          >
            {card1Value}
          </span>
        </div>
        <div className="card card-2">
          <img 
            src={`/svgs/${fullCard2Suit}.svg`}
            alt={fullCard2Suit} 
            className="card-suit"
            style={{ color: getSuitColor(fullCard2Suit) }}
          />
          <span 
            className="card-value"
            style={{ color: getSuitColor(fullCard2Suit) }}
          >
            {card2Value}
          </span>
        </div>
      </div>

      {isDealer && (
        <div className="dealer-button">
          <img src="/svgs/dealer.svg" alt="Dealer" width="24" height="24" />
        </div>
      )}

      {shouldShowBet && (
        <div className="bet-container">
          <img 
            src="/svgs/chip.svg"
            alt="Betting chip"
            className="bet-chip"
            width="24" 
            height="24"
          />
          <span className="bet-amount">
            {displayBet}<span className="bet-unit">bb</span>
          </span>
        </div>
      )}

      {showPotWon && (
        <div className="pot-won-container">
          <img 
            src="/svgs/pot-won.svg"
            alt="Pot Won Icon"
            className="pot-won-icon"
            width="57" 
            height="42"
          />
          <span className="pot-won-amount">
            <span className="pot-won-value">{potSize}</span>
            <span className="pot-won-unit">bb</span>
          </span>
        </div>
      )}
    </div>
  );
};

// Helper function for suit colors
const getSuitColor = (suit) => {
  const SUIT_COLORS = {
    hearts: '#E57373',    
    clubs: '#4CAF50',     
    diamonds: '#497CAB',  
    spades: '#424242'     
  };
  return SUIT_COLORS[suit];
};

export default HeroSeat;